// Brisk money pages — pure display rules (labels, colours, filters, Excel rows). The server decides every figure
// (backend service/briskV2WalletService.js); nothing here adds money up or decides anything.
import moment from "moment";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { STATUS_LABELS as CERTIFICATE_STATUS } from "../BriskCertificateList/briskV2ListUtils";

export const LEDGER_TYPE_FILTERS = [
  { _id: "all", label: "All", value: "" },
  { _id: "topup", label: "Top-up", value: "TOPUP" },
  { _id: "online", label: "Brisk online top-up", value: "BRISK_TOPUP" },
  { _id: "certificate", label: "Certificate", value: "CERTIFICATE" },
  { _id: "certificateRefund", label: "Certificate refund", value: "CERTIFICATE_REFUND" },
  { _id: "policyPa", label: "Online Policy PA", value: "POLICY_PA" },
  { _id: "policyPaRefund", label: "Online Policy PA refund", value: "POLICY_PA_REFUND" },
  { _id: "removed", label: "Removed", value: "REMOVED" },
];

export const HISTORY_STATUS_FILTERS = [
  { _id: "all", label: "All", value: "" },
  { _id: "awaiting", label: "Awaiting payment", value: "AWAITING_PAYMENT" },
  { _id: "credited", label: "Added to wallet", value: "CREDITED" },
  { _id: "paidTest", label: "Paid on TEST (not added)", value: "PAID_TEST" },
  { _id: "failed", label: "Failed", value: "FAILED" },
  { _id: "unknown", label: "Not known yet", value: "UNKNOWN" },
  { _id: "manualAdded", label: "Top-up by admin", value: "MANUAL_ADDED" },
  { _id: "manualRemoved", label: "Removed by admin", value: "MANUAL_REMOVED" },
];

const TOPUP_STATUS = {
  INITIATING: "Starting",
  AWAITING_PAYMENT: "Awaiting payment",
  CREDITED: "Added to wallet",
  PAID_TEST: "Paid on TEST (not added)",
  FAILED: "Failed",
  UNKNOWN: "Not known yet",
  NEEDS_REVIEW: "Needs admin",
  ADDED: "Added",
  REMOVED: "Removed",
};

const CERTIFICATE_KINDS = ["CERTIFICATE", "CERTIFICATE_REFUND"];

/** A ledger row's status in words: the certificate's own status, the top-up's, or the Online Policy's. */
export const ledgerStatusText = (row) => {
  if (!row || !row.status) return "-";
  if (CERTIFICATE_KINDS.includes(row.kind)) return CERTIFICATE_STATUS[row.status] || row.status;
  return TOPUP_STATUS[row.status] || row.status;
};

export const topupStatusText = (status) => TOPUP_STATUS[status] || status || "-";

export const statusColorOf = (status) => {
  if (["CREATED", "CREDITED", "ADDED", "Complete"].includes(status)) return "green";
  if (["FAILED", "REMOVED", "Cancelled"].includes(status)) return "red";
  if (["CANCELLED", "PAID_TEST", "DRAFT"].includes(status)) return "grey";
  return "#b54708"; // waiting: payment, a Recheck or an admin
};

/** "1256.00" -> "₹1,256.00" (the server's figure, only printed). */
export const rupees = (value) => {
  if (value === "" || value === null || value === undefined) return "-";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const dayParam = (date) => (date && moment(date).isValid() ? moment(date).format("YYYY-MM-DD") : "");
export const whenText = (at) => (at ? moment(at).format("DD-MM-YYYY hh:mm A") : "-");
const num = (value) => (value === "" || value === null || value === undefined ? "" : Number(value));

export const LEDGER_EXCEL_HEADERS = [
  "Transaction Date",
  "Transaction Type",
  "User Name",
  "Certificate Number",
  "Online Policy Number",
  "Customer Name",
  "Chassis No",
  "Total Premium",
  "Net Premium",
  "Transaction ID",
  "Credit",
  "Debit",
  "Balance",
  "Status",
  "Brisk",
  "Remarks",
];

/** The Transaction Report / Statement rows as Excel rows (the agency first for the super admin). */
export const ledgerExcelRows = (rows, { withAgency = false } = {}) =>
  (rows || []).map((r) => [
    ...(withAgency ? [r.agencyName || ""] : []),
    whenText(r.at),
    r.typeLabel || "",
    r.userName || "",
    r.certificateNo || "",
    r.policyNo || "",
    r.customerName || "",
    r.chassisNo || "",
    num(r.totalPremium),
    num(r.netPremium),
    r.transactionId || r.orderId || "",
    num(r.credit),
    num(r.debit),
    num(r.balanceAfter),
    ledgerStatusText(r),
    r.apiEnv || "",
    r.remarks || "",
  ]);

export const HISTORY_EXCEL_HEADERS = ["Transaction Date", "User", "Type", "Order ID", "Transaction ID", "Status", "Amount", "Added to wallet", "Brisk", "Remarks"];

export const historyExcelRows = (rows, { withAgency = false } = {}) =>
  (rows || []).map((r) => [
    ...(withAgency ? [r.agencyName || ""] : []),
    whenText(r.at),
    r.userName || "",
    r.source === "ONLINE" ? "Brisk online" : "Admin",
    r.orderId || "",
    r.transactionId || "",
    topupStatusText(r.status),
    num(r.amount),
    num(r.credited),
    r.apiEnv || "",
    r.remarks || "",
  ]);

/** Saves an .xlsx with a bold header row (same look as the other reports). */
export const saveExcel = async (fileName, headers, rows) => {
  const book = new ExcelJS.Workbook();
  const sheet = book.addWorksheet("Report");
  sheet.addRow(headers);
  sheet.getRow(1).font = { bold: true };
  rows.forEach((r) => sheet.addRow(r));
  sheet.columns.forEach((c) => {
    c.width = 18;
  });
  const buffer = await book.xlsx.writeBuffer();
  saveAs(new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), fileName);
};
